import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import SearchFilter from 'components/SearchFilter';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { buildProvisionedRoleEditPath } from 'router/routesAliases';
import { getProvisionedJobRolesList } from 'http/hyperAdminProvisioned';
import { transformTableData } from 'utils';
import getTableConfig from 'pages/Tasks/helpers/getTableConfig';

const PAGE_SIZE = 20;

const useStyles = makeStyles(theme => ({
  toolbar: {
    marginBottom: theme.spacing(2),
  },
}));

const ProvisionedRolesList = ({ companyId, returnTab }) => {
  const classes = useStyles();
  const history = useHistory();
  const tableConfig = useMemo(() => getTableConfig(), []);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState({ field: 'id', dir: 'desc' });

  const [listState, fetchList] = useAsyncFn(async () => {
    return getProvisionedJobRolesList(companyId, {
      page,
      pageSize: PAGE_SIZE,
      ...(search ? { search } : {}),
      ...(sorting.field ? { [sorting.field]: sorting.dir.toUpperCase() } : {}),
    });
  }, [companyId, page, search, sorting]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const items = listState.value?.items || [];
  const totalCount = listState.value?.count || 0;
  const [columns, rows] = useMemo(() => transformTableData(tableConfig, items), [tableConfig, items]);

  const openEditPage = useCallback(
    roleId => {
      localStorage.setItem('companyEditActiveTab', String(returnTab));
      history.push({
        pathname: buildProvisionedRoleEditPath(companyId, roleId),
        state: { returnTab },
      });
    },
    [companyId, history, returnTab],
  );

  const tableActions = useMemo(
    () => [
      {
        type: 'icon',
        icon: 'edit',
        onClick: event => {
          openEditPage(Number(event.currentTarget.dataset.key));
        },
      },
    ],
    [openEditPage],
  );

  const handleSearch = (value, searchAlias) => {
    if (searchAlias === 'search') {
      setPage(0);
      setSearch(value || '');
    }
  };

  const handleClearSearch = () => {
    setPage(0);
    setSearch('');
  };

  const handleSorting = (field, dir) => {
    setSorting({ field, dir });
  };

  return (
    <Box>
      <Box className={classes.toolbar}>
        <SearchFilter searchAlias="search" onSearch={handleSearch} onClearSearch={handleClearSearch} />
      </Box>
      <ModalBusyContainer busy={listState.loading}>
        <AppTable
          columns={columns}
          data={rows}
          totalCount={totalCount}
          rowsPerPage={PAGE_SIZE}
          onChangePage={setPage}
          onColumnSorting={handleSorting}
          currentPage={page}
          isLazyLoading
          actions={tableActions}
          actionsColumnWidth="80px"
        />
      </ModalBusyContainer>
    </Box>
  );
};

ProvisionedRolesList.propTypes = {
  companyId: PropTypes.number.isRequired,
  returnTab: PropTypes.number.isRequired,
};

export default ProvisionedRolesList;
